import { inject, Injectable } from '@angular/core';
import type { Params } from '@angular/router';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Buffer } from 'buffer';
import {
  catchError,
  EMPTY,
  filter,
  forkJoin,
  iif,
  map,
  of,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';

import {
  type ApplicationType as Application,
  DownloadService,
  FILE_DOCUMENT_TYPE,
  FileService,
  INFERENCE_TYPE,
  PAGINATION_SIZES,
  SearchService,
  StorageAiService,
  StorageDataSort,
} from '@evc/platform';
import { buildPreviewUrl, getPreviewType } from '@evc/platform';
import { ConfirmModalComponent, errorTypes, ModalService } from '@evc/web-components';
import { I18nService } from '@shared/services/i18n/i18n.service';

import {
  browseTargetActions,
  BrowseTargetModalComponent,
  FileFormModalActions,
  FileFormModalComponent,
} from '@app/modals';
import { BreadcrumbActions, DefaultActions, FileActions, ToastActions } from '@app/store/actions';
import { RenameItemSuccessProps } from '@app/store/actions/file/file.action';
import {
  getBreadcrumb,
  getDataSearch,
  getRouterData,
  getRouterParams,
  getRouterUrlArray,
} from '@app/store/selectors';
import { ApplicationEnum } from '@app/types/application.enum';
import type { BackendError } from '@app/types/error/backend-error.type';
import { FileErrorEnum } from '@app/types/error/file-error.enum';
import type { FileItem } from '@app/types/file-item.type';

type FileActionPayload = {
  items: FileItem[];
  parentId?: string;
  prompt?: boolean;
};

@Injectable()
export class FileEffects {
  readonly #actions$ = inject(Actions);
  readonly #fileService = inject(FileService);
  readonly #storageAiService = inject(StorageAiService);
  readonly #downloadService = inject(DownloadService);
  readonly #store = inject(Store);
  readonly #router = inject(Router);
  readonly #translator = inject(TranslateService);
  readonly #modalService = inject(ModalService);
  readonly #i18nService = inject(I18nService);
  readonly #searchService = inject(SearchService);

  loadData$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.loadData),
      withLatestFrom(
        this.#store.pipe(select(getRouterParams)),
        this.#store.pipe(select(getRouterUrlArray)),
      ),
      map(
        ([
          { page, sort, search },
          params,
          state,
        ]: [
          { page: number; sort: StorageDataSort; search: string },
          Params,
          string[],
        ]) => ({
          parentId: (params as { id?: string }).id,
          page,
          sort,
          search,
          isTrash: state[0] === 'trash',
        }),
      ),
      switchMap(({ parentId, page, sort, search, isTrash }) =>
        this.#fileService
          .browse$(
            {
              pageNumber: page,
              pageSize: PAGINATION_SIZES.BROWSE,
              searchQuery: search,
              searchEverywhere: !!search,
              parentId,
              ...(isTrash ? { searchInTrash: true } : {}),
            },
            sort,
          )
          .pipe(
            map(({ pageNumber, items, count, totalPages }) =>
              FileActions.loadDataSuccess({ data: items, page: pageNumber, count, totalPages }),
            ),
            catchError((error: BackendError) => of(FileActions.loadDataFail({ error }))),
          ),
      ),
    ),
  );

  loadDataFail$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.loadDataFail),
      withLatestFrom(this.#store.pipe(select(getRouterUrlArray))),
      map(
        ([
          action,
          state,
        ]: [
          { error: BackendError },
          string[],
        ]) => ({ error: action.error, state }),
      ),
      tap(({ error: { errorType }, state }: { error: BackendError; state: string[] }) => {
        if (errorType === FileErrorEnum.FileHasBeenRemoved) {
          this.#router.navigate([`/${state[0]}`]);
        }
      }),
      switchMap(({ error: { errorType } }: { error: BackendError; state: string[] }) => [
        ToastActions.addToastError({
          message: errorType ? `file.error.${errorType}` : 'toast.error',
        }),
      ]),
    ),
  );

  loadFolder$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.loadFolder),
      switchMap(({ id }: { id: string }) =>
        this.#fileService.getFile$(id).pipe(
          map((folder) => {
            this.#store.dispatch(FileActions.setSelectedItems({ items: [folder] }));
            this.#store.dispatch(BreadcrumbActions.addItem({ folder }));

            return FileActions.loadFolderSuccess({ folder });
          }),
          catchError((error: BackendError) => of(FileActions.loadFolderFail({ error }))),
        ),
      ),
    ),
  );

  loadFolderSuccess$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.loadFolderSuccess),
      withLatestFrom(this.#store.pipe(select(getBreadcrumb))),
      filter(
        ([
          ,
          breadcrumb,
        ]: [
          { folder: FileItem },
          FileItem[],
        ]) => !breadcrumb || breadcrumb.length < 2,
      ),
      switchMap(
        ([
          { folder },
        ]: [
          { folder: FileItem },
          FileItem[],
        ]) => [BreadcrumbActions.loadBreadcrumb({ id: folder.id })],
      ),
    ),
  );

  loadFolderFail$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(FileActions.loadFolderFail),
      tap(() => {
        const root = this.#router.url.startsWith('/trash') ? '/trash' : '/drive';
        void this.#router.navigate([root], { replaceUrl: true });
      }),
      map(({ error }: { error: BackendError }) =>
        ToastActions.addToastError({
          message: error.errorType ? `file.error.${error.errorType}` : 'file.error.global',
        }),
      ),
    ),
  );

  createFolder$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.createFolder),
      map((payload: { name: string }) => payload.name),
      withLatestFrom(this.#store.pipe(select(getRouterParams))),
      map(
        ([
          name,
          params,
        ]: [
          name: string,
          parans: Params,
        ]) => ({
          name,
          parentId: params.id,
        }),
      ),
      switchMap((partialFolder: Partial<FileItem>) =>
        this.#modalService
          .open$<FileItem>(FileFormModalComponent, {
            action: FileFormModalActions.CREATE,
            file: partialFolder,
            options: { catchOnCancel: false },
          })
          .pipe(
            switchMap((folder: FileItem) =>
              this.#fileService.createFolder$({
                name: folder.name,
                parentId: folder.parentId ?? undefined,
              }),
            ),
            map((folder) => FileActions.createFolderSuccess({ folder })),
            catchError((error: BackendError) =>
              of(FileActions.createFolderFail({ error, item: partialFolder })),
            ),
          ),
      ),
    ),
  );

  createFolderSuccess$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.createFolderSuccess),
      withLatestFrom(
        this.#store.pipe(select(getDataSearch)),
        this.#store.pipe(select(getRouterUrlArray)),
      ),
      tap(
        ([
          _folderAction,
          _search,
          state,
        ]: [
          unknown,
          string,
          string[],
        ]) => {
          // TODO: only drive for now... Think about it when supporting "add folder" in many lists
          if (state[0] !== 'drive') {
            this.#router.navigate(['/']);
          }
        },
      ),
      switchMap(
        ([
          { folder },
          search,
        ]: [
          { folder: FileItem },
          string,
          string[],
        ]) =>
          iif(
            () => !!search,
            [
              FileActions.setNewIdToFind({ idToFind: folder.id }),
              FileActions.clearSearch(),
            ],
            [
              FileActions.setNewIdToFind({ idToFind: folder.id }),
              FileActions.loadData({ page: 0, search: '' }),
            ],
          ),
      ),
    ),
  );

  createFolderFail$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.createFolderFail),
      filter(({ error }) => error !== errorTypes.CANCEL),
      switchMap(({ error, item }) => [
        ToastActions.addToastError({
          message: error.errorType ? `file.error.${error.errorType}` : 'file.error.global',
          extra: {
            action: {
              text: this.#i18nService.t('actions.retry'),
              callback: () => this.#store.dispatch(FileActions.createFolder(item)),
            },
          },
        }),
      ]),
    ),
  );

  renameItem$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.renameItem),
      switchMap(({ item }: { item: FileItem }) =>
        this.#modalService
          .open$<FileItem>(FileFormModalComponent, {
            action: FileFormModalActions.RENAME,
            file: item,
            options: { catchOnCancel: false },
          })
          .pipe(
            switchMap((file: FileItem) =>
              this.#fileService.renameFile$({ id: file.id, newName: file.name }),
            ),
            map((savedItem) =>
              FileActions.renameItemSuccess({
                item: savedItem,
                previous: item,
              }),
            ),
            catchError((error: BackendError) => of(FileActions.renameItemFail({ error, item }))),
          ),
      ),
    ),
  );

  renameItemSuccess$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.renameItemSuccess),
      switchMap(({ previous }: RenameItemSuccessProps) => [
        ToastActions.addToastSuccess({
          message: 'actions.rename.success',
          params: { name: previous.name },
        }),
        FileActions.loadData({ page: 0 }),
      ]),
    ),
  );

  renameItemFail$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.renameItemFail),
      filter(({ error }) => error.message !== errorTypes.CANCEL),
      map((action: { error: BackendError; item: FileItem }) => [
        action.error.errorType,
        action.item,
      ]),
      switchMap(([errorType, item]) => [
        ToastActions.addToastError({
          message: errorType ? `file.error.${errorType}` : 'file.error.global',
          extra: {
            action: {
              text: this.#i18nService.t('actions.retry'),
              callback: () => this.#store.dispatch(FileActions.renameItem({ item })),
            },
          },
        }),
      ]),
    ),
  );

  deleteItems$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.deleteItems),
      switchMap(({ items }: FileActionPayload) =>
        forkJoin(items.map((item) => this.#fileService.softDeleteFile$(item.id))).pipe(
          map((deletedItems) => FileActions.deleteItemsSuccess({ items: deletedItems })),
          catchError((error: BackendError) => of(FileActions.deleteItemsFail({ error }))),
        ),
      ),
    ),
  );

  deleteItemsSuccess$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.deleteItemsSuccess),
      switchMap(({ items }: FileActionPayload) => [
        ToastActions.addToastSuccess({
          message: items.length > 1 ? 'file.delete.items' : 'file.delete.item',
          params: { name: items.map((i) => i.name).join(', ') },
          extra: {
            duration: 10000,
            action: {
              text: this.#i18nService.t('file.delete.undo'),
              callback: () => {
                this.#store.dispatch(FileActions.restoreItems({ items, undo: true }));
              },
            },
          },
        }),
      ]),
    ),
  );

  deleteItemsFail$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.deleteItemsFail),
      map((action: { error: BackendError }) => action.error),
      switchMap(({ errorType }: BackendError) => [
        ToastActions.addToastError({
          message: errorType ? `file.error.${errorType}` : 'file.error.global',
        }),
      ]),
    ),
  );

  deleteAllTrash$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.deleteAllTrash),
      switchMap(() =>
        this.#modalService
          .open$<FileItem>(ConfirmModalComponent, {
            icon: 'trash',
            title: this.#i18nService.t('actions.deleteAll.label'),
            content: this.#i18nService.t('labels.permanentAction'),
            action: this.#i18nService.t('actions.deleteAll.action'),
            cancelLabel: this.#i18nService.t('actions.cancel'),
            isError: true,
            options: { catchOnCancel: false },
          })
          .pipe(
            switchMap(() => this.#fileService.emptyTrash$()),
            map(({ totalDeletedFiles, totalDeletedDirectories }) =>
              FileActions.deleteAllTrashSuccess({ totalDeletedFiles, totalDeletedDirectories }),
            ),
            catchError((error: BackendError) => of(FileActions.deleteAllTrashFail({ error }))),
          ),
      ),
    ),
  );

  deleteAllTrashSuccess$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.deleteAllTrashSuccess),
      switchMap(({ totalDeletedFiles, totalDeletedDirectories }) => {
        const totalDeleted: number = totalDeletedFiles + totalDeletedDirectories;

        return [
          ToastActions.addToastSuccess({
            message:
              totalDeleted === 1 ? 'trash.permanentDelete.item' : 'trash.permanentDelete.items',
            params: { count: totalDeleted },
          }),
        ];
      }),
    ),
  );

  deleteAllTrashFail$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.deleteAllTrashFail),
      filter(({ error }) => error.message !== errorTypes.CANCEL),
      map((action: { error: BackendError }) => action.error),
      switchMap(() => [
        ToastActions.addToastError({
          message: 'trash.error.global',
        }),
      ]),
    ),
  );

  permanentlyDeleteItems$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.permanentlyDeleteItems),
      switchMap(({ items }: FileActionPayload) =>
        this.#modalService
          .open$<FileItem>(ConfirmModalComponent, {
            icon: 'trash',
            title: this.#i18nService.t('actions.delete.label', { p11n: items.length }),
            content: this.#i18nService.t('labels.permanentAction'),
            action: this.#i18nService.t('actions.delete.action'),
            cancelLabel: this.#i18nService.t('actions.cancel'),
            isError: true,
            options: { catchOnCancel: false },
          })
          .pipe(
            switchMap(() =>
              forkJoin(items.map((item) => this.#fileService.permanentlyDeleteFile$(item.id))),
            ),
            map((deletedItems) =>
              FileActions.permanentlyDeleteItemsSuccess({ items: deletedItems }),
            ),
            catchError((error: BackendError) =>
              of(FileActions.permanentlyDeleteItemsFail({ error })),
            ),
          ),
      ),
    ),
  );

  permanentlyDeleteItemsSuccess$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.permanentlyDeleteItemsSuccess),
      switchMap(({ items }: FileActionPayload) => [
        ToastActions.addToastSuccess({
          message: items.length > 1 ? 'trash.permanentDelete.items' : 'trash.permanentDelete.item',
          params: { name: items.map((i) => i.name).join(', '), count: items.length },
        }),
      ]),
    ),
  );

  permanentlyDeleteItemsFail$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.permanentlyDeleteItemsFail),
      filter(({ error }) => error.message !== errorTypes.CANCEL),
      map((action: { error: BackendError }) => action.error),
      switchMap(({ errorType }: BackendError) => [
        ToastActions.addToastError({
          message: errorType ? `trash.error.${errorType}` : 'trash.error.global',
        }),
      ]),
    ),
  );

  restoreItems$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.restoreItems),
      withLatestFrom(this.#store.pipe(select(getRouterData))),
      map(
        ([
          { items, undo },
          data,
        ]: [
          { items: FileItem[]; undo?: boolean },
          { id: string },
        ]) => ({
          items,
          undo,
          location: data.id,
        }),
      ),
      switchMap(
        ({ items, undo, location }: { items: FileItem[]; undo?: boolean; location?: string }) =>
          forkJoin(items.map((item) => this.#fileService.restoreFile$(item.id))).pipe(
            map((response) => {
              // Extract the restored files with their updated properties (including parentId)
              const restoredFiles = response.map((item) => item.file);
              // Files that had their recovery directory changed to root
              const changedToRootFiles = response
                .filter((i) => i.recoveryDirectoryChangedToRoot)
                .map((i) => i.file);

              return FileActions.restoreItemsSuccess({
                items: restoredFiles,
                undo,
                toNotify: changedToRootFiles,
                location,
              });
            }),
            catchError((error: BackendError) => of(FileActions.restoreItemsFail({ error }))),
          ),
      ),
    ),
  );

  restoreItemsSuccess$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.restoreItemsSuccess),
      switchMap(
        ({
          items,
          undo,
          toNotify,
        }: {
          items: FileItem[];
          undo?: boolean;
          toNotify?: FileItem[];
        }) => {
          const successMessage: string = this.#translator.instant(
            undo
              ? 'file.delete.cancelled'
              : items.length > 1
                ? 'file.delete.restoreMany'
                : 'file.delete.restore',
            { name: items.map((i) => i.name).join(', ') },
          );

          const notifyMessage: string = toNotify?.length
            ? this.#translator.instant(
                toNotify.length > 1 ? 'file.delete.restoreManyRoot' : 'file.delete.restoreRoot',
                { name: toNotify.map((i) => i.name).join(', ') },
              )
            : '';

          return [
            ToastActions.addToastSuccess({
              message: `${successMessage} ${notifyMessage}`,
              extra: {
                duration: 5000 + (toNotify?.length || 0) * 500,
              },
            }),
          ];
        },
      ),
    ),
  );

  restoreItemsFail$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.restoreItemsFail),
      map((action: { error: BackendError }) => action.error),
      switchMap(({ errorType }: BackendError) => [
        ToastActions.addToastError({
          message: errorType ? `file.error.${errorType}` : 'file.error.global',
        }),
      ]),
    ),
  );

  copyItems$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.copyItems),
      switchMap(({ items, prompt, parentId }: FileActionPayload) => {
        if (!items.length) {
          return [
            ToastActions.addToastWarning({ message: `file.error.${FileErrorEnum.EmptySelection}` }),
            FileActions.copyItemsSuccess({ items: [] }),
          ];
        }

        const folders: FileItem[] = items.filter((i) => i.type === FILE_DOCUMENT_TYPE.FOLDER);
        if (folders.length) {
          return [
            ToastActions.addToastWarning({
              message: `file.error.${FileErrorEnum.FolderCopyNotSupported}`,
            }),
            FileActions.copyItemsSuccess({ items: [] }),
          ];
        }

        const mayBrowseLocation$ = prompt
          ? this.#modalService.open$<string>(BrowseTargetModalComponent, {
              action: browseTargetActions.COPY,
              items,
              options: { catchOnCancel: false },
            })
          : of(parentId);

        return mayBrowseLocation$.pipe(
          switchMap((targetParentId: string) =>
            forkJoin(
              items.map((item) =>
                this.#fileService.copyFile$({
                  sourceFileId: item.id,
                  destinationParentId: targetParentId,
                }),
              ),
            ).pipe(
              map((returnedItems) =>
                FileActions.copyItemsSuccess({ items: returnedItems, parentId: targetParentId }),
              ),
            ),
          ),
          catchError((error: BackendError) => of(FileActions.copyItemsFail({ error }))),
        );
      }),
    ),
  );

  copyItemsSuccess$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.copyItemsSuccess),
      filter(({ items }) => !!items.length),
      switchMap(({ items }: { items: FileItem[] }) => [
        ToastActions.addToastSuccess({
          message: items.length > 1 ? 'file.copy.items' : 'file.copy.item',
        }),
        // Reload data to refresh the UI with the new files
        FileActions.loadData({ page: 0 }),
      ]),
    ),
  );

  copyItemsFail$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.copyItemsFail),
      filter(({ error }) => error.message !== errorTypes.CANCEL),
      map((action: { error: BackendError }) => action.error),
      switchMap(({ errorType }: BackendError) => [
        ToastActions.addToastError({
          message: errorType ? `file.error.${errorType}` : 'file.error.global',
        }),
      ]),
    ),
  );

  moveItems$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.moveItems),
      switchMap(({ items, prompt, parentId }: FileActionPayload) => {
        const mayBrowseLocation$ = prompt
          ? this.#modalService.open$<string>(BrowseTargetModalComponent, {
              action: browseTargetActions.MOVE,
              items,
              options: { catchOnCancel: false },
            })
          : of(parentId);

        return mayBrowseLocation$.pipe(
          switchMap((targetParentId: string) =>
            forkJoin(
              items.map((item) =>
                this.#fileService.moveFile$({
                  fileId: item.id,
                  destinationParentId: targetParentId,
                }),
              ),
            ).pipe(
              map((returnedItems) =>
                FileActions.moveItemsSuccess({ items: returnedItems, parentId: targetParentId }),
              ),
            ),
          ),
          catchError((error: BackendError) => of(FileActions.moveItemsFail({ error }))),
        );
      }),
    ),
  );

  moveItemsSuccess$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.moveItemsSuccess),
      switchMap(({ items }: FileActionPayload) => [
        ToastActions.addToastSuccess({
          message: items.length > 1 ? 'file.move.items' : 'file.move.item',
        }),
        // Reload data to refresh the UI with the new files
        FileActions.loadData({ page: 0 }),
      ]),
    ),
  );

  moveItemsFail$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.moveItemsFail),
      filter(({ error }) => error.message !== errorTypes.CANCEL),
      map((action: { error: BackendError }) => action.error),
      switchMap(({ errorType }: BackendError) => [
        ToastActions.addToastError({
          message: errorType ? `file.error.${errorType}` : 'file.error.global',
        }),
      ]),
    ),
  );

  /**
   * Legacy methods waiting for implementation on the backend
   */
  // lockItems$: any = createEffect((): any => this.#actions$.pipe(
  //   ofType(FileActions.lockItems),
  //   switchMap(({ items }: FileActionPayload) =>
  //     this.#fileService.lockItems(items).pipe(
  //       map((returnedItems) => FileActions.lockItemsSuccess({ items: returnedItems })),
  //       catchError((error: BackendError) => of(FileActions.lockItemsFail({ error }))),
  //     ),
  //   ),
  // ));

  // lockItemsSuccess$: any = createEffect((): any => this.#actions$.pipe(
  //   ofType(FileActions.lockItemsSuccess),
  //   switchMap(({ items }: FileActionPayload) => [
  //     ToastActions.addToastSuccess({
  //       message: items.length > 1 ? 'file.lock.items' : 'file.lock.item',
  //     }),
  //   ]),
  // ));

  // lockItemsFail$: any = createEffect((): any => this.#actions$.pipe(
  //   ofType(FileActions.lockItemsFail),
  //   map((action: { error: BackendError }) => action.error),
  //   switchMap(({ errorType }: BackendError) => [
  //     ToastActions.addToastError({
  //       message: errorType ? `file.error.${errorType}` : 'file.error.global',
  //     }),
  //   ]),
  // ));

  // unlockItems$: any = createEffect((): any => this.#actions$.pipe(
  //   ofType(FileActions.unlockItems),
  //   switchMap(({ items }: FileActionPayload) =>
  //     this.#fileService.unlockItems(items).pipe(
  //       map(() => FileActions.unlockItemsSuccess({ items })),
  //       catchError((error: BackendError) => of(FileActions.unlockItemsFail({ error }))),
  //     ),
  //   ),
  // ));

  // unlockItemsSuccess$: any = createEffect((): any => this.#actions$.pipe(
  //   ofType(FileActions.unlockItemsSuccess),
  //   switchMap(({ items }: FileActionPayload) => [
  //     ToastActions.addToastSuccess({
  //       message: items.length > 1 ? 'file.unlock.items' : 'file.unlock.item',
  //     }),
  //   ]),
  // ));

  // unlockItemsFail$: any = createEffect((): any => this.#actions$.pipe(
  //   ofType(FileActions.unlockItemsFail),
  //   map((action: { error: BackendError }) => action.error),
  //   switchMap(({ errorType }: BackendError) => [
  //     ToastActions.addToastError({
  //       message: errorType ? `file.error.${errorType}` : 'file.error.global',
  //     }),
  //   ]),
  // ));

  startDefectAnalysis$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.startDefectAnalysis),
      switchMap(({ item }: { item: FileItem }) =>
        this.#storageAiService
          .createJob({ fileId: item.id, inferenceTypes: [INFERENCE_TYPE.FLAW_DETECTION] })
          .pipe(
            map(() => FileActions.startDefectAnalysisSuccess({ item })),
            catchError((error: BackendError) => of(FileActions.startDefectAnalysisFail({ error }))),
          ),
      ),
    ),
  );

  startDefectAnalysisSuccess$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.startDefectAnalysisSuccess),
      switchMap(() => [
        ToastActions.addToastSuccess({
          message: 'file.defectAnalysis.start.item',
        }),
      ]),
    ),
  );

  startDefectAnalysisFail$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.startDefectAnalysisFail),
      map((action: { error: BackendError }) => action.error),
      switchMap(({ errorType }: BackendError) => [
        ToastActions.addToastError({
          message: errorType ? `file.error.${errorType}` : 'file.error.global',
        }),
      ]),
    ),
  );

  startAnomalyAnalysis$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.startAnomalyAnalysis),
      switchMap(({ item }: { item: FileItem }) =>
        this.#storageAiService
          .createJob({ fileId: item.id, inferenceTypes: [INFERENCE_TYPE.ANOMALY_DETECTION] })
          .pipe(
            map(() => FileActions.startAnomalyAnalysisSuccess({ item })),
            catchError((error: BackendError) =>
              of(FileActions.startAnomalyAnalysisFail({ error })),
            ),
          ),
      ),
    ),
  );

  startAnomalyAnalysisSuccess$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.startAnomalyAnalysisSuccess),
      switchMap(() => [
        ToastActions.addToastSuccess({
          message: 'file.anomalyAnalysis.start.item',
        }),
      ]),
    ),
  );

  startAnomalyAnalysisFail$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.startAnomalyAnalysisFail),
      map((action: { error: BackendError }) => action.error),
      switchMap(({ errorType }: BackendError) => [
        ToastActions.addToastError({
          message: errorType ? `file.error.${errorType}` : 'file.error.global',
        }),
      ]),
    ),
  );

  /**
   * Legacy methods waiting for implementation on the backend
   */
  // loadItemMetadata$: any = createEffect((): any => this.#actions$.pipe(
  //   ofType(FileActions.loadItemMetadata),
  //   switchMap(({ item }: { item: FileItem }) => {
  //     const fileContentType: string = item.latestVersion?.contentType;
  //     const ndeType = this.#configService.get('ndetype');
  //     const canItemHaveMetadata: boolean = item.type.toLowerCase() === 'file' && fileContentType === ndeType;
  //     if (canItemHaveMetadata) {
  //       return this.#fileService.loadItemMetadata(item.id).pipe(
  //         map((metadata: any) => FileActions.loadItemMetadataSuccess({ metadata })),
  //         catchError((error: BackendError) => of(FileActions.loadItemMetadataFail({ error }))),
  //       );
  //     }

  //     return [FileActions.clearMetadata()];
  //   }),
  // ));

  // loadItemMetadataFail$: any = createEffect((): any => this.#actions$.pipe(
  //   ofType(FileActions.loadItemMetadataFail),
  //   map((action: { error: BackendError, metadata: Metadata }) => action.error),
  //   switchMap(({ errorType }: BackendError) => [
  //     ToastActions.addToastError({
  //       message: errorType ? `file.error.${errorType}` : 'file.error.global',
  //     }),
  //     FileActions.clearMetadata(),
  //   ]),
  // ));

  openFile$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.openFile),
      switchMap(({ file, app }: { file: FileItem; app: Application }) =>
        app.code.toLowerCase() === ApplicationEnum.ZIP
          ? [FileActions.downloadItems({ items: [file] })]
          : [FileActions.openInApp({ file, app })],
      ),
    ),
  );

  openInApp$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.openInApp),
      switchMap(({ file, app }: { file: FileItem; app: Application }) =>
        this.#downloadService.downloadUrl$(file.id).pipe(
          tap(({ sasUri }: { sasUri: string }) => {
            const B64filename = this.#encodeStringInBase64(file.name);

            const link = app.openWithUrl
              ? this.#replaceInUrl(app.openWithUrl, {
                  downloadUrl: sasUri,
                  fileid: file.id,
                  B64filename,
                })
              : app.launchUrl;

            window.open(link, '_blank');
          }),
          map(() => DefaultActions.noAction()),
          catchError((error: BackendError) => of(FileActions.openInAppFail({ error }))),
        ),
      ),
    ),
  );

  openInAppFail$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.openInAppFail),
      map((action: { error: BackendError }) => action.error),
      switchMap(({ errorType }: BackendError) => [
        ToastActions.addToastError({
          message: errorType ? `file.error.${errorType}` : 'file.error.global',
        }),
      ]),
    ),
  );

  previewFile$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.previewFile),
      switchMap(({ file }: { file: FileItem }) => {
        const previewType = getPreviewType(file.latestVersion?.contentType ?? '');

        if (!previewType) {
          return [FileActions.downloadItems({ items: [file] })];
        }

        return this.#downloadService.downloadUrl$(file.id, { inline: true }).pipe(
          tap(({ sasUri }: { sasUri: string }) => {
            const url = buildPreviewUrl(sasUri, previewType);
            window.open(url, '_blank');
          }),
          map(() => DefaultActions.noAction()),
          catchError((error: BackendError) => of(FileActions.previewFileFail({ error }))),
        );
      }),
    ),
  );

  previewFileFail$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.previewFileFail),
      map((action: { error: BackendError }) => action.error),
      switchMap(({ errorType }: BackendError) => [
        ToastActions.addToastError({
          message: errorType ? `file.error.${errorType}` : 'file.error.global',
        }),
      ]),
    ),
  );

  downloadItems$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.downloadItems),
      switchMap(({ items }: FileActionPayload) =>
        this.#downloadService.downloadFiles$(items).pipe(
          map((result) => {
            if (!result) {
              return DefaultActions.noAction();
            }

            const { done, file, percentage } = result;

            return done
              ? FileActions.downloadItemSuccess({ item: file })
              : percentage
                ? FileActions.downloadItemProgress({ item: file, percentage })
                : DefaultActions.noAction();
          }),
          // TODO: BackendError in the middle of error management
          catchError((error) =>
            of(
              FileActions.downloadItemFail({
                error: 'download.failed.file',
                item: error.item,
                params: { name: error.item?.name },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  downloadItemFail$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.downloadItemFail),
      switchMap(({ error, params }: { error: string; params: Record<string, string> }) => [
        ToastActions.addToastError({ message: error, extra: { duration: 0 }, params }),
      ]),
    ),
  );

  downloadItemsByUrl$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.downloadItemsByUrl),
      switchMap(({ items }: FileActionPayload) =>
        this.#downloadService.downloadItemsByUrl$(items).pipe(
          map(() => FileActions.downloadItemsByUrlSuccess({ items })),
          catchError((error: BackendError) => of(FileActions.downloadItemsByUrlFail({ error }))),
        ),
      ),
    ),
  );

  downloadItemsByUrlFail$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.downloadItemsByUrlFail),
      map((action: { error: BackendError }) => action.error),
      switchMap(({ errorType }: BackendError) => [
        ToastActions.addToastError({
          message: errorType ? `file.error.${errorType}` : 'download.failed.global',
        }),
      ]),
    ),
  );

  clearSearch$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(FileActions.clearSearch),
      tap(() => this.#searchService.clearSearch()),
      switchMap(() => EMPTY),
    ),
  );

  #encodeStringInBase64(str: string): string {
    try {
      return Buffer.from(str, 'binary').toString('base64');
    } catch (e) {
      console.error(`Buffer crash: ${e}`);

      return '';
    }
  }

  #replaceInUrl(url: string, parameters: Record<string, string>): string {
    let replacedUrl: string = url;

    for (const key in parameters) {
      replacedUrl = replacedUrl.replace(`[${key}]`, encodeURIComponent(parameters[key]));
    }

    return replacedUrl;
  }
}
